import { useEffect, useState } from 'react'

/**
 * A hook for managing smooth mount/unmount animations for panels and overlays.
 *
 * This hook handles the timing of mounting and animating elements to ensure
 * smooth transitions. It follows a two-step animation pattern:
 * 1. Mount the element first (isMounted = true)
 * 2. Trigger animation after a brief delay (shouldAnimateIn = true)
 * 3. On close, reverse animation first, then unmount after animation completes
 *
 * @param isOpen - Boolean controlling whether the panel should be open/visible
 * @param options - Configuration options
 * @param options.animationDelay - Delay in ms before starting the enter animation (default: 10ms)
 * @param options.animationDuration - Duration in ms for the animation (default: 400ms)
 * @param options.onAnimationStart - Callback invoked when the panel starts opening
 * @param options.onAnimationComplete - Callback invoked when the panel finishes closing
 * @returns Object with isMounted and shouldAnimateIn boolean states
 *
 * @example
 * ```tsx
 * const { isMounted, shouldAnimateIn } = useAnimatedPanel(isOpen, {
 *   onAnimationStart: () => console.log('Opening'),
 *   onAnimationComplete: () => console.log('Closed')
 * })
 *
 * return (
 *   <Transition show={isMounted}>
 *     <div className={clsx(
 *       'transition-transform duration-400',
 *       shouldAnimateIn ? 'translate-x-0' : 'translate-x-full'
 *     )}>
 *       Panel content
 *     </div>
 *   </Transition>
 * )
 * ```
 */
export const useAnimatedPanel = (
  isOpen: boolean,
  options: {
    animationDelay?: number
    animationDuration?: number
    onAnimationStart?: () => void
    onAnimationComplete?: () => void
  } = {}
) => {
  const { animationDelay = 10, animationDuration = 400, onAnimationStart, onAnimationComplete } = options

  const [isMounted, setIsMounted] = useState(false)
  const [shouldAnimateIn, setShouldAnimateIn] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true)
      onAnimationStart?.()

      const animateInTimeout = setTimeout(() => {
        setShouldAnimateIn(true)
      }, animationDelay)

      return () => clearTimeout(animateInTimeout)
    }

    setShouldAnimateIn(false)

    const unmountTimeout = setTimeout(() => {
      setIsMounted(false)
      onAnimationComplete?.()
    }, animationDuration)

    return () => clearTimeout(unmountTimeout)
  }, [isOpen, animationDelay, animationDuration, onAnimationStart, onAnimationComplete])

  return { isMounted, shouldAnimateIn }
}